import clsx from 'clsx';

import type { ReactNode } from 'react';

import type {
  Props as InteractiveButtonProps,
  Variant as InteractiveButtonVariant,
} from '@/components/InteractiveButton';
import { InteractiveButton } from '@/components/InteractiveButton';
import type {
  Props as InteractiveLinkProps,
  Variant as InteractiveLinkVariant,
} from '@/components/InteractiveLink';
import { InteractiveLink } from '@/components/InteractiveLink';

import styles from './interactive-group.module.scss';

type AsElement = 'button' | 'link';

type InteractiveAsLink = InteractiveLinkProps & { as: 'link' };
type InteractiveAsButton = InteractiveButtonProps & { as?: 'button' };

export type GroupElement = InteractiveAsLink | InteractiveAsButton;

export interface Props {
  primary: GroupElement;
  secondary?: GroupElement;
  tertiary?: GroupElement;
  narrow?: boolean;
  theme?: 'light' | 'dark';
  className?: string;
}

export function InteractiveGroup({
  primary,
  secondary,
  tertiary,
  narrow = false,
  theme = 'light',
  className,
}: Props) {
  const groupClassName = clsx([
    `${styles['interactive-group']} ${narrow && styles['interactive-group--narrow']}`,
    className,
  ]);
  const tertiaryClassName = `${styles['interactive-group__tertiary']} ${
    narrow && styles['interactive-group__tertiary--narrow']
  }`;

  return (
    <>
      <div className={groupClassName}>
        {getInteractiveElement(
          primary.as || 'button',
          { ...primary, hasIcon: true },
          'primary',
          primary.theme ?? theme
        )}
        {secondary &&
          getInteractiveElement(
            secondary.as || 'link',
            { ...secondary, hasIcon: true },
            'secondary',
            secondary.theme ?? theme
          )}
        {tertiary && (
          <div className={tertiaryClassName}>
            {getInteractiveElement(
              'link',
              { ...tertiary, hasIcon: true },
              'secondary',
              tertiary.theme ?? theme
            )}
          </div>
        )}
      </div>
    </>
  );
}

function getInteractiveElement(
  as: AsElement,
  props: GroupElement,
  defaultVariant: 'primary' | 'secondary',
  theme: 'light' | 'dark'
): ReactNode {
  switch (as) {
    case 'button':
      return (
        <InteractiveButton
          {...(props as InteractiveButtonProps)}
          variant={(props.variant as InteractiveButtonVariant) || defaultVariant}
          theme={theme}
        />
      );
    case 'link':
      return (
        <InteractiveLink
          {...(props as InteractiveLinkProps)}
          variant={(props.variant as InteractiveLinkVariant) || defaultVariant}
          theme={theme}
        />
      );
  }
}
